import {
  Breadcrumbs,
  Button,
  Content,
  LoadingBlock,
  Modal,
  Textarea,
} from '@orobix/orobix-fe-components'
import { AxiosError } from 'axios'
import { Formik } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import DatasetName from '../../components/DatasetName/DatasetName'
import ObservationsSelectField from '../../components/ObservationsSelectField/ObservationsSelectField'
import TrainingInfo from '../../components/TrainingInfo/TrainingInfo'
import { useDispatch, useStore } from '../../store/store'
import { endpoints, fetchProduct, fetchProject } from '../../store/storeActions'
import { ObservationCompact } from '../../types/observation'
import { get, post, update } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { vars } from '../../utils/appConfig'
import { validateDatasetName } from '../../utils/datasetValidation'
import { locations } from '../../utils/locations'
import { DatasetImageFormValues } from '../TrainingNew/DatasetImageTraining'
import useAddDatasetStyles from './AddDatasetPage.styles'
import DatasetsAside from './DatasetsAside'
import DatasetsNotes from './DatasetsNotes'
import useStyles from './DatasetsRoot.styles'

interface RouteParams {
  productId: string
  projectId: string
  datasetId?: string
}

interface DatasetResponse {
  id: number
  name: string
  notes?: string
  observations?: { id: number }[]
  images?: { id: number }[]
}

type AddDatasetFormValues = DatasetImageFormValues

const AddDatasetPage = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const addDatasetClasses = useAddDatasetStyles()
  const history = useHistory()
  const { product, project } = useStore()
  const dispatch = useDispatch()
  const { productId, projectId, datasetId } = useParams<RouteParams>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [datasetObservations, setDatasetObservations] = useState<
    { id: number }[]
  >([])
  const [initialDatasetName, setInitialDatasetName] = useState<string>('')
  const [isLoadingDataset, setIsLoadingDataset] = useState<boolean>(false)
  // Track which dataset image IDs have been loaded (visible) in the UI
  const [loadedImageIds, setLoadedImageIds] = useState<Set<number>>(new Set())
  const [effectiveSelectedCount, setEffectiveSelectedCount] = useState<number>(
    0
  )
  const handleEffectiveCountChange = useCallback((count: number) => {
    setEffectiveSelectedCount(count)
  }, [])
  const [globalObservations, setGlobalObservations] = useState<
    ObservationCompact[]
  >([])
  const [datasetNotes, setDatasetNotes] = useState('')
  const [notesModalOpen, setNotesModalOpen] = useState(false)
  const [notesModalValue, setNotesModalValue] = useState('')

  // Get dataset id from URL params (for edit mode)
  const editingDatasetId = datasetId ? Number(datasetId) : undefined
  const isEditMode = !!editingDatasetId

  useEffect(() => {
    if (!project) {
      fetchProject(dispatch, Number(projectId))
    }
  }, [dispatch, project, projectId])

  useEffect(() => {
    fetchProduct(dispatch, Number(productId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch dataset data when in edit mode
  useEffect(() => {
    if (isEditMode && editingDatasetId) {
      setIsLoadingDataset(true)
      // Reset loaded IDs tracking when switching datasets
      setLoadedImageIds(new Set())
      get<DatasetResponse>(`/dataset/${editingDatasetId}/`)
        .then((response) => {
          // Set the dataset name
          setInitialDatasetName(response.name || '')
          setDatasetNotes(response.notes || '')

          const rawObservations = response.observations || response.images || []

          // Transform raw IDs to objects with id properties
          const observations = rawObservations.map(
            (obs: number | { id: number }) => ({
              id: typeof obs === 'number' ? obs : obs.id,
            })
          )
          setDatasetObservations(observations)
        })
        .catch(() => {
          setDatasetObservations([])
          setInitialDatasetName('')
          setDatasetNotes('')
        })
        .finally(() => {
          setIsLoadingDataset(false)
        })
    } else {
      setDatasetObservations([])
      setInitialDatasetName('')
      setDatasetNotes('')
      setLoadedImageIds(new Set())
    }
  }, [isEditMode, editingDatasetId])

  useEffect(() => {
    if (isEditMode && editingDatasetId) {
      get<{ results: ObservationCompact[]; count: number }>(
        `/observations/?product=${productId}&dataset=${editingDatasetId}&limit=20000&mode=no_image_data`
      )
        .then((data) => {
          setGlobalObservations(data.results || [])
        })
        .catch(() => {
          setGlobalObservations([])
        })
    } else {
      setGlobalObservations([])
    }
  }, [isEditMode, editingDatasetId, productId])

  const handleSaveDataset = async (values: AddDatasetFormValues) => {
    if (!values.name.trim()) {
      toast.error(t('Dataset name is required'))
      return
    }

    setIsSubmitting(true)

    try {
      // Get IDs from currently selected observations
      const selectedImageIds = values.selectedObservations.map((obs) => obs.id)

      // In edit mode, preserve original dataset images that haven't been loaded yet
      // This prevents data loss when user saves without scrolling through all images
      let finalImageIds: number[]
      if (isEditMode && datasetObservations.length > 0) {
        // Find original dataset images that were NOT loaded (not visible to user)
        const unloadedOriginalIds = datasetObservations
          .filter((obs) => !loadedImageIds.has(obs.id))
          .map((obs) => obs.id)

        // Merge: user's selection on loaded images + preserved unloaded original images
        const selectedSet = new Set(selectedImageIds)
        const uniqueUnloadedIds = unloadedOriginalIds.filter(
          (id) => !selectedSet.has(id)
        )
        finalImageIds = [...selectedImageIds, ...uniqueUnloadedIds]

        // eslint-disable-next-line no-console
        console.log('Dataset save merge:', {
          originalCount: datasetObservations.length,
          loadedCount: loadedImageIds.size,
          selectedCount: selectedImageIds.length,
          preservedUnloadedCount: uniqueUnloadedIds.length,
          finalCount: finalImageIds.length,
        })
      } else {
        // For new datasets, just use the selected images
        finalImageIds = selectedImageIds
      }

      if (isEditMode && editingDatasetId) {
        await update(`/dataset/${editingDatasetId}/`, {
          name: values.name.trim(),
          notes: datasetNotes,
          product_id: Number(productId),
          observations_ids: finalImageIds,
        })
        toast.success(
          t('Dataset successfully updated', {
            defaultValue: 'Dataset successfully updated',
          })
        )
      } else {
        const created = await post<DatasetResponse>(endpoints.createDataset, {
          name: values.name.trim(),
          product_id: Number(productId),
          observations_ids: finalImageIds,
        })
        if (datasetNotes && created?.id) {
          await update(`/dataset/${created.id}/`, { notes: datasetNotes })
        }
        toast.success(t('Dataset successfully created'))
      }

      history.push(locations.datasets(Number(projectId), Number(productId)))
    } catch (error) {
      handleApiErrors(error as AxiosError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackToDatasets = () => {
    history.push(locations.datasets(Number(projectId), Number(productId)))
  }

  const formInitialValues = useMemo(
    () => ({
      name: initialDatasetName,
      selectedObservations: [] as ObservationCompact[],
      dataset: { value: '', label: '' },
    }),
    [initialDatasetName]
  )

  if (!product || !project || isLoadingDataset) {
    return <LoadingBlock message={t('Loading')} />
  }

  return (
    <Content
      aside={<DatasetsAside />}
      asideWidth={vars.asideWidth}
      childrenClassName={classes.content}
      breadcrumbs={
        <Breadcrumbs
          elements={[
            <Link to={locations.projects}>{t('Projects')}</Link>,
            <Link to={locations.products(Number(projectId))}>
              {project.name}
            </Link>,
            <Link
              to={locations.trainings(Number(projectId), Number(productId))}
            >
              {product.name}
            </Link>,
            <Link to={locations.datasets(Number(projectId), Number(productId))}>
              {t('Datasets')}
            </Link>,
            isEditMode ? t('Dataset edit') : t('New Dataset'),
          ]}
        />
      }
    >
      <div className={classes.datasets}>
        <Formik
          enableReinitialize
          initialValues={formInitialValues}
          validate={(values) => {
            const errors: Record<string, string> = {}
            const result = validateDatasetName(values.name)

            if (!result.valid) {
              errors.name = t(result.errorKey! as any)
            }
            return Object.keys(errors).length > 0 ? errors : {}
          }}
          onSubmit={handleSaveDataset}
        >
          {({ values, errors, isValid }) => (
            <>
              <div className={classes.list}>
                {/* Header Section */}
                <div className={addDatasetClasses.headerContainer}>
                  <h1 className={addDatasetClasses.headerTitle}>
                    {isEditMode ? t('Dataset edit') : t('New dataset')}
                  </h1>
                  <p className={addDatasetClasses.headerDescription}>
                    {isEditMode
                      ? 'Dataset can be renamed and images can be added or removed from current dataset'
                      : t(
                          'To create a new dataset, digit a name then select at least 1 image.',
                          {
                            defaultValue:
                              'To create a new dataset, digit a name then select at least 1 image.',
                          }
                        )}
                  </p>
                </div>

                {/* Form Section */}
                <div className={addDatasetClasses.formContainer}>
                  {/* Dataset Name Input and Action Buttons */}
                  <div className={addDatasetClasses.nameAndButtonsContainer}>
                    <div className={addDatasetClasses.nameFieldContainer}>
                      <DatasetName fieldName="name" />
                    </div>

                    <div className={addDatasetClasses.buttonsContainer}>
                      <Button
                        label={t('Cancel')}
                        type="default"
                        onClick={handleBackToDatasets}
                      />
                      <Button
                        label={t('Save')}
                        type="action"
                        disabled={
                          isSubmitting ||
                          !isValid ||
                          !values.name.trim() ||
                          (isEditMode
                            ? effectiveSelectedCount === 0
                            : values.selectedObservations.length === 0)
                        }
                        onClick={() =>
                          handleSaveDataset(values as AddDatasetFormValues)
                        }
                        loading={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Images Selection - Uses window scroll */}
                  <div className={addDatasetClasses.imagesSelectionContainer}>
                    <ObservationsSelectField
                      name="selectedObservations"
                      productType={product.type}
                      productId={Number(productId)}
                      showDatasetManager={false}
                      useWindowScroll
                      autoSelectFirst
                      initialSelectedObservations={
                        isEditMode ? datasetObservations : []
                      }
                      isEditMode={isEditMode}
                      inDatasetSection
                      onLoadedIdsChange={
                        isEditMode ? setLoadedImageIds : undefined
                      }
                      isSaving={isSubmitting}
                      onEffectiveCountChange={handleEffectiveCountChange}
                    />
                  </div>

                  {/* Error Messages */}
                  {!!(
                    errors.selectedObservations &&
                    values.selectedObservations.length > 0
                  ) && (
                    <div className={addDatasetClasses.errorMessage}>
                      {errors.selectedObservations}
                    </div>
                  )}
                </div>
              </div>

              <div className={classes.sidePanel}>
                <TrainingInfo
                  selectedObservations={
                    isEditMode
                      ? globalObservations
                      : values.selectedObservations
                  }
                  productType={product.type}
                />
                {isEditMode ? (
                  <>
                    <DatasetsNotes
                      notes={datasetNotes}
                      onClick={() => {
                        setNotesModalValue(datasetNotes)
                        setNotesModalOpen(true)
                      }}
                    />
                    {notesModalOpen && (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault()
                          setDatasetNotes(notesModalValue)
                          setNotesModalOpen(false)
                        }}
                      >
                        <Modal
                          size="md"
                          onCloseRequest={() => setNotesModalOpen(false)}
                          title={t('Dataset notes')}
                          footer={
                            <Button
                              label={t('Save')}
                              type="action"
                              buttonType="submit"
                            />
                          }
                        >
                          <Textarea
                            name="notes-modal"
                            id="dataset-notes-modal"
                            type="text"
                            value={notesModalValue}
                            onChange={(
                              e: React.ChangeEvent<HTMLTextAreaElement>
                            ) => setNotesModalValue(e.target.value)}
                            maxLength={255}
                          />
                        </Modal>
                      </form>
                    )}
                  </>
                ) : (
                  <Textarea
                    name="notes"
                    id="dataset-notes"
                    label={t('Notes')}
                    type="text"
                    value={datasetNotes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setDatasetNotes(e.target.value)
                    }
                    maxLength={255}
                    rows={3}
                  />
                )}
              </div>
            </>
          )}
        </Formik>
      </div>
    </Content>
  )
}

export default AddDatasetPage
